const PIPELINE_STATUSES = new Set(["running", "ok", "error"]);

export const RMS_PIPELINE_STAGES = Object.freeze([
  "cancellation_curves",
  "demand_forecast",
  "daily_features",
  "recommendations",
  "long_range_price_guidance",
]);

const PIPELINE_STAGE_SET = new Set(RMS_PIPELINE_STAGES);
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIMESTAMP_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;
const REASON_CODE_RE = /^[a-z0-9_]+$/;
const SNAPSHOT_SAFE_STALE_REASON_CODES = new Set([
  "newer_pipeline_running",
  "newer_pipeline_failed",
]);

function isPlainObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasOwn(value, field) {
  return Object.prototype.hasOwnProperty.call(value, field);
}

function normalizeId(value) {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
  }
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return /^[1-9]\d*$/.test(normalized) ? normalized : null;
}

function normalizeDateOnly(value) {
  if (typeof value !== "string" || !DATE_ONLY_RE.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value
    ? null
    : value;
}

function normalizeTimestamp(value, { nullable = false } = {}) {
  if (value == null && nullable) return null;
  if (typeof value !== "string" || !ISO_TIMESTAMP_RE.test(value)) return undefined;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? value : undefined;
}

function normalizeStageArray(value, { allowEmpty = true } = {}) {
  if (!Array.isArray(value) || (!allowEmpty && value.length === 0)) return null;
  if (
    value.some((stage) => typeof stage !== "string" || !PIPELINE_STAGE_SET.has(stage)) ||
    new Set(value).size !== value.length
  ) {
    return null;
  }
  return [...value];
}

function normalizeStaleReasons(value) {
  if (!Array.isArray(value)) return null;
  const normalized = [];
  const seen = new Set();

  for (const reason of value) {
    if (!isPlainObject(reason)) return null;
    const keys = Object.keys(reason).sort();
    if (keys.length !== 2 || keys[0] !== "code" || keys[1] !== "stage") return null;
    const code = typeof reason.code === "string" ? reason.code.trim() : "";
    const stage = reason.stage == null ? null : reason.stage;
    if (
      !code ||
      code.length > 80 ||
      !REASON_CODE_RE.test(code) ||
      (stage !== null && (typeof stage !== "string" || !PIPELINE_STAGE_SET.has(stage)))
    ) {
      return null;
    }
    const key = `${code}::${stage || ""}`;
    if (!seen.has(key)) {
      seen.add(key);
      normalized.push({ code, stage });
    }
  }

  return normalized;
}

function normalizeLatestAttempt(value) {
  if (value == null) return null;
  if (!isPlainObject(value)) return undefined;
  const requiredFields = ["id", "status", "started_at", "finished_at", "error_message"];
  if (requiredFields.some((field) => !hasOwn(value, field))) return undefined;
  const id = normalizeId(value.id);
  const status = typeof value.status === "string" ? value.status.trim().toLowerCase() : "";
  const startedAt = normalizeTimestamp(value.started_at);
  const finishedAt = normalizeTimestamp(value.finished_at, { nullable: true });
  const errorMessage =
    value.error_message == null
      ? null
      : typeof value.error_message === "string" && value.error_message.trim()
      ? value.error_message.trim().slice(0, 500)
      : undefined;

  if (!id || !PIPELINE_STATUSES.has(status) || !startedAt || finishedAt === undefined) {
    return undefined;
  }
  if (errorMessage === undefined) return undefined;

  const lifecycleIsValid =
    (status === "running" && finishedAt === null && errorMessage === null) ||
    (status === "ok" && Boolean(finishedAt) && errorMessage === null) ||
    (status === "error" && Boolean(finishedAt) && Boolean(errorMessage));
  if (!lifecycleIsValid) return undefined;

  return {
    id,
    status,
    startedAt,
    finishedAt,
    errorMessage,
  };
}

function normalizePipelineContract(value) {
  if (!isPlainObject(value)) return null;
  const requiredFields = [
    "id",
    "status",
    "date_from",
    "date_to",
    "started_at",
    "finished_at",
    "published_at",
    "expected_stages",
    "completed_stages",
    "is_published",
    "is_current",
    "is_stale",
    "stale_reasons",
    "latest_attempt",
  ];
  if (requiredFields.some((field) => !hasOwn(value, field))) return null;

  const id = normalizeId(value.id);
  const status = typeof value.status === "string" ? value.status.trim().toLowerCase() : "";
  const dateFrom = normalizeDateOnly(value.date_from);
  const dateTo = normalizeDateOnly(value.date_to);
  const startedAt = normalizeTimestamp(value.started_at);
  const finishedAt = normalizeTimestamp(value.finished_at, { nullable: true });
  const publishedAt = normalizeTimestamp(value.published_at, { nullable: true });
  const expectedStages = normalizeStageArray(value.expected_stages, { allowEmpty: false });
  const completedStages = normalizeStageArray(value.completed_stages);
  const staleReasons = normalizeStaleReasons(value.stale_reasons);
  const latestAttempt = normalizeLatestAttempt(value.latest_attempt);
  const booleansAreValid =
    typeof value.is_published === "boolean" &&
    typeof value.is_current === "boolean" &&
    typeof value.is_stale === "boolean";
  const expectedStagesAreExact =
    expectedStages?.length === RMS_PIPELINE_STAGES.length &&
    RMS_PIPELINE_STAGES.every((stage) => expectedStages.includes(stage));
  const allStagesAreCompleted =
    completedStages?.length === RMS_PIPELINE_STAGES.length &&
    RMS_PIPELINE_STAGES.every((stage) => completedStages.includes(stage));
  const lifecycleIsValid =
    (status === "running" &&
      finishedAt === null &&
      publishedAt === null &&
      value.is_published === false) ||
    (status === "ok" &&
      Boolean(finishedAt) &&
      Boolean(publishedAt) &&
      value.is_published === true &&
      allStagesAreCompleted) ||
    (status === "error" &&
      Boolean(finishedAt) &&
      publishedAt === null &&
      value.is_published === false);

  if (
    !id ||
    !PIPELINE_STATUSES.has(status) ||
    !dateFrom ||
    !dateTo ||
    dateFrom > dateTo ||
    !startedAt ||
    finishedAt === undefined ||
    publishedAt === undefined ||
    !expectedStages ||
    !completedStages ||
    !staleReasons ||
    latestAttempt === undefined ||
    !booleansAreValid ||
    !expectedStagesAreExact ||
    !lifecycleIsValid ||
    completedStages.some((stage) => !expectedStages.includes(stage))
  ) {
    return null;
  }

  return {
    id,
    status,
    dateFrom,
    dateTo,
    startedAt,
    finishedAt,
    publishedAt,
    expectedStages,
    completedStages,
    isPublished: value.is_published,
    isCurrent: value.is_current,
    isStale: value.is_stale,
    staleReasons,
    latestAttempt,
  };
}

function normalizeUnavailablePipelineContract(value) {
  if (!isPlainObject(value)) return null;
  const requiredFields = [
    "id",
    "status",
    "date_from",
    "date_to",
    "started_at",
    "finished_at",
    "published_at",
    "expected_stages",
    "completed_stages",
    "is_published",
    "is_current",
    "is_stale",
    "stale_reasons",
    "latest_attempt",
  ];
  if (requiredFields.some((field) => !hasOwn(value, field))) return null;

  const expectedStages = normalizeStageArray(value.expected_stages, { allowEmpty: false });
  const completedStages = normalizeStageArray(value.completed_stages);
  const staleReasons = normalizeStaleReasons(value.stale_reasons);
  const latestAttempt = normalizeLatestAttempt(value.latest_attempt);
  const expectedStagesAreExact =
    expectedStages?.length === RMS_PIPELINE_STAGES.length &&
    RMS_PIPELINE_STAGES.every((stage) => expectedStages.includes(stage));

  if (
    value.id !== null ||
    value.status !== "unavailable" ||
    value.date_from !== null ||
    value.date_to !== null ||
    value.started_at !== null ||
    value.finished_at !== null ||
    value.published_at !== null ||
    !expectedStagesAreExact ||
    !completedStages ||
    completedStages.length !== 0 ||
    value.is_published !== false ||
    value.is_current !== false ||
    value.is_stale !== true ||
    !staleReasons ||
    !staleReasons.some((reason) => reason.code === "pipeline_unavailable") ||
    latestAttempt === undefined
  ) {
    return null;
  }

  return { staleReasons, latestAttempt };
}

function addIssue(issues, issue) {
  const key = `${issue.code}::${issue.stage || ""}::${issue.section || ""}`;
  if (!issues.some((entry) => entry.key === key)) {
    issues.push({ ...issue, key });
  }
}

function normalizeRecordGroups(recordGroups) {
  return Array.isArray(recordGroups) ? recordGroups : [];
}

function buildUnverifiedStatus({
  pipeline = null,
  issues = [],
  latestAttempt = null,
  includeContractIssue = true,
} = {}) {
  const normalizedIssues = [];
  if (includeContractIssue) {
    addIssue(normalizedIssues, { code: "pipeline_contract_invalid", stage: null });
  }
  issues.forEach((issue) => addIssue(normalizedIssues, issue));
  return {
    verified: false,
    coherent: false,
    stale: true,
    state: "unverified",
    tone: "danger",
    labelKey: "Pipeline not verified",
    issues: normalizedIssues,
    pipeline,
    latestAttempt,
    snapshotAligned: false,
    safeToRender: false,
  };
}

export function resolveRmsPipelineStatus(
  pipeline,
  { relatedPipelines = [], recordGroups = [] } = {}
) {
  const normalized = normalizePipelineContract(pipeline);
  if (!normalized) {
    const unavailable = normalizeUnavailablePipelineContract(pipeline);
    if (unavailable) {
      return buildUnverifiedStatus({
        issues: unavailable.staleReasons,
        latestAttempt: unavailable.latestAttempt,
        includeContractIssue: false,
      });
    }
    return buildUnverifiedStatus();
  }

  let contractValid = true;
  let stale = normalized.isStale;
  let snapshotAligned = true;
  const issues = [];
  normalized.staleReasons.forEach((reason) => addIssue(issues, reason));
  if (
    normalized.staleReasons.some((reason) =>
      !SNAPSHOT_SAFE_STALE_REASON_CODES.has(reason.code)
    )
  ) {
    snapshotAligned = false;
  }
  if (normalized.staleReasons.some((reason) => reason.code === "pipeline_contract_invalid")) {
    contractValid = false;
  }

  if (normalized.status !== "ok") {
    stale = true;
    snapshotAligned = false;
    addIssue(issues, {
      code: normalized.status === "running" ? "pipeline_running" : "pipeline_error",
      stage: null,
    });
  }
  if (!normalized.isPublished || !normalized.publishedAt) {
    stale = true;
    snapshotAligned = false;
    addIssue(issues, { code: "pipeline_not_published", stage: null });
  }
  if (!normalized.isCurrent) {
    stale = true;
    addIssue(issues, { code: "pipeline_not_current", stage: null });
  }
  if (normalized.latestAttempt && normalized.latestAttempt.id !== normalized.id) {
    if (normalized.latestAttempt.status === "running") {
      stale = true;
      addIssue(issues, { code: "newer_pipeline_running", stage: null });
    } else if (normalized.latestAttempt.status === "error") {
      stale = true;
      addIssue(issues, { code: "newer_pipeline_failed", stage: null });
    } else {
      stale = true;
      addIssue(issues, { code: "pipeline_not_current", stage: null });
    }
  }

  const allCanonicalStagesExpected = RMS_PIPELINE_STAGES.every((stage) =>
    normalized.expectedStages.includes(stage)
  );
  const everyExpectedStageCompleted = normalized.expectedStages.every((stage) =>
    normalized.completedStages.includes(stage)
  );
  if (!allCanonicalStagesExpected || !everyExpectedStageCompleted) {
    stale = true;
    snapshotAligned = false;
    addIssue(issues, { code: "pipeline_incomplete", stage: null });
  }
  if (normalized.isStale && !issues.length) {
    snapshotAligned = false;
    addIssue(issues, { code: "pipeline_marked_stale", stage: null });
  }

  (Array.isArray(relatedPipelines) ? relatedPipelines : []).forEach((related) => {
    const relatedPipeline = normalizePipelineContract(related?.pipeline);
    if (!relatedPipeline) {
      contractValid = false;
      snapshotAligned = false;
      return;
    }
    if (
      relatedPipeline.staleReasons.some(
        (reason) => reason.code === "pipeline_contract_invalid"
      )
    ) {
      contractValid = false;
      snapshotAligned = false;
    }
    if (relatedPipeline.id !== normalized.id) {
      stale = true;
      snapshotAligned = false;
      addIssue(issues, {
        code: "pipeline_endpoint_mismatch",
        stage: null,
        section: related?.label || null,
      });
    }
    if (
      relatedPipeline.dateFrom !== normalized.dateFrom ||
      relatedPipeline.dateTo !== normalized.dateTo ||
      relatedPipeline.startedAt !== normalized.startedAt ||
      relatedPipeline.finishedAt !== normalized.finishedAt ||
      relatedPipeline.publishedAt !== normalized.publishedAt
    ) {
      stale = true;
      snapshotAligned = false;
      addIssue(issues, {
        code: "pipeline_endpoint_mismatch",
        stage: null,
        section: related?.label || null,
      });
    }
    const relatedStageSetIsComplete =
      RMS_PIPELINE_STAGES.every((stage) => relatedPipeline.expectedStages.includes(stage)) &&
      relatedPipeline.expectedStages.every((stage) =>
        relatedPipeline.completedStages.includes(stage)
      );
    if (
      relatedPipeline.status !== "ok" ||
      !relatedPipeline.isPublished ||
      !relatedPipeline.publishedAt ||
      relatedPipeline.isStale ||
      !relatedPipeline.isCurrent ||
      !relatedStageSetIsComplete ||
      relatedPipeline.staleReasons.length > 0
    ) {
      stale = true;
      if (
        relatedPipeline.status !== "ok" ||
        !relatedPipeline.isPublished ||
        !relatedPipeline.publishedAt ||
        !relatedStageSetIsComplete ||
        relatedPipeline.staleReasons.some((reason) =>
          !SNAPSHOT_SAFE_STALE_REASON_CODES.has(reason.code)
        ) ||
        (relatedPipeline.isStale && !relatedPipeline.staleReasons.length)
      ) {
        snapshotAligned = false;
      }
      relatedPipeline.staleReasons.forEach((reason) =>
        addIssue(issues, { ...reason, section: related?.label || null })
      );
      if (!relatedPipeline.staleReasons.length) {
        addIssue(issues, {
          code: "pipeline_section_stale",
          stage: null,
          section: related?.label || null,
        });
      }
    }
  });

  normalizeRecordGroups(recordGroups).forEach((group) => {
    if (group?.requireRecord && !isPlainObject(group.record)) {
      contractValid = false;
      snapshotAligned = false;
      return;
    }
    if (group?.requireRecordsArray && !Array.isArray(group.records)) {
      contractValid = false;
      snapshotAligned = false;
      return;
    }
    const records = Array.isArray(group?.records)
      ? group.records
      : group?.record
      ? [group.record]
      : [];
    records.forEach((record) => {
      if (!isPlainObject(record)) {
        contractValid = false;
        snapshotAligned = false;
        return;
      }
      if (group.requirePipelineRunId && !hasOwn(record, "pipeline_run_id")) {
        contractValid = false;
        snapshotAligned = false;
        return;
      }
      if (hasOwn(record, "pipeline_run_id")) {
        const recordPipelineId = normalizeId(record.pipeline_run_id);
        if (!recordPipelineId) {
          stale = true;
          snapshotAligned = false;
          addIssue(issues, {
            code: "legacy_run_without_pipeline",
            stage: group.stage || null,
            section: group.label || null,
          });
        } else if (recordPipelineId !== normalized.id) {
          stale = true;
          snapshotAligned = false;
          addIssue(issues, {
            code: "pipeline_section_mismatch",
            stage: group.stage || null,
            section: group.label || null,
          });
        }
      }
      if (hasOwn(record, "is_stale")) {
        if (typeof record.is_stale !== "boolean") {
          contractValid = false;
          snapshotAligned = false;
        } else if (record.is_stale) {
          stale = true;
          addIssue(issues, {
            code: "pipeline_section_stale",
            stage: group.stage || null,
            section: group.label || null,
          });
        }
      }
    });
  });

  if (!contractValid) {
    return buildUnverifiedStatus({
      pipeline: normalized,
      issues,
      latestAttempt: normalized.latestAttempt,
    });
  }

  const coherent = !stale && issues.length === 0;
  return {
    verified: true,
    coherent,
    stale: !coherent,
    state: coherent ? "coherent" : "stale",
    tone: coherent ? "success" : "danger",
    labelKey: coherent ? "Coherent pipeline" : "Stale pipeline",
    issues,
    pipeline: normalized,
    latestAttempt: normalized.latestAttempt,
    snapshotAligned,
    safeToRender: snapshotAligned,
  };
}
